// Angular imports
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';

// CC-Common imports
import {
  CCMessageBoxIcon,
  IconConstants,
  Utils
} from '@therealmanage/ciranet-ui-common';
import { RxjsUtils } from '@therealmanage/ciranet-ui-common';

// Component imports
import { RecaptchaBaseComponent } from '../../_base/recaptcha-base.component';

// Service imports
import { UserService } from '../../_services/data-services/user.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { EnvironmentConfigService } from '../../_services/environment-config.service';
import { HelperService } from '../../_services/shared-services/helper.service';

// Model imports
import { AddContactInfoCustomFieldRawDto } from '../../_models/registration/add-contact-info-custom-field-raw-dto';
import { CreateUserParameterDto } from '../../_models/create-user-parameter-dto';
import { ResultStatusDto } from '../../_models/result-status-dto';
import { RegistrationEditorResultDto } from '../../_models/registration/registration-editor-result-dto';

@Component({
  selector: 'app-signup-email-options',
  templateUrl: './signup-email-options.component.html',
  styleUrls: ['./signup-email-options.component.scss'],
  standalone: false
})
export class SignupEmailOptionsComponent
  extends RecaptchaBaseComponent
  implements OnInit
{
  @Input() userInfo: CreateUserParameterDto;
  @Input() registrationEditor: RegistrationEditorResultDto;
  @Input() isEmbedded = false;

  @Output() onEmailCommunicationUpdate =
    new EventEmitter<CreateUserParameterDto>();
  @Output() onPreviousClick = new EventEmitter<void>();
  @Output() onBackToLogin = new EventEmitter<void>();

  customFieldsData: Array<any> = [];

  isSmallScreen = false;
  isMediumScreen = false;

  isTextCommunicationRequired = false;
  loadingVisible = false;
  isUserCreated = false;
  userRecaptchaToken: string;
  isRecaptchaResolved = false;

  // Email preference toggles
  electronicStatementsEnabled = false;
  standardNoticesEnabled = false;
  emergencyNoticesEnabled = true;

  backToLoginIcon = IconConstants.BACK_TO_LOGIN;

  constructor(
    private router: Router,
    private screenService: ScreenService,
    private userService: UserService,
    private helperService: HelperService,
    protected override errorService: ServerErrorService,
    protected override envConfig: EnvironmentConfigService
  ) {
    super(errorService, envConfig);
  }

  ngOnInit() {
    this.updateScreenSize();
    RxjsUtils.safeSubscribe(this.screenService.changed, () =>
      this.updateScreenSize()
    );

    // Update header title
    setTimeout(() => {
      this.helperService.subjectLoginHeaderText.next(
        'Email&nbsp;Communication Preferences'
      );
    }, 0);

    this.isTextCommunicationRequired =
      this.registrationEditor.phoneCustomDataFields.length > 0 ? true : false;
    this.userRecaptchaToken = this.userInfo.recaptchaToken;

    this.prepareCommunications();

    this.checkRecaptchaValidation();
  }

  btnPreviousClick(): void {
    // Reset header title when going back
    this.helperService.subjectLoginHeaderText.next('Register Account');
    this.onPreviousClick.emit();
  }

  btnNextClick(): void {
    // Set email content from userInfo.email (entered in register-details step)
    if (this.userInfo.contactInfo?.emailData) {
      this.userInfo.contactInfo.emailData.content = this.userInfo.email;
    }
    this.prepareCommunicationsCustomFields();
    this.onEmailCommunicationUpdate.emit(this.userInfo);
  }

  btnBackToLogin(): void {
    if (this.isEmbedded) {
      this.onBackToLogin.emit();
    } else {
      this.router.navigate(['user', 'login']);
    }
  }

  btnRegisterClick(): void {
    // Set email content from userInfo.email (entered in register-details step)
    if (this.userInfo.contactInfo?.emailData) {
      this.userInfo.contactInfo.emailData.content = this.userInfo.email;
    }
    this.prepareCommunicationsCustomFields();
    this.loadingVisible = true;

    RxjsUtils.safeSubscribe(
      this.userService.createUser(this.userInfo),
      (result: ResultStatusDto) => {
        this.resetRecaptcha();
        this.loadingVisible = false;
        if (result.isSuccess == false) {
          this.messageBox.showWithFixedWidthAndIcon(
            CCMessageBoxIcon.error,
            'Error',
            `There is an error creating user. ${result.message}`,
            320
          );
        } else {
          this.isUserCreated = true;
        }
      },
      (error) => {
        this.resetRecaptcha();
        this.loadingVisible = false;
        this.processApiError(error);
      }
    );
    // }
  }

  // Override
  override processApiError(error: any): void {
    const apiError = error.error;
    if (
      apiError &&
      apiError.message &&
      apiError.message.indexOf('captcha') !== -1
    ) {
      this.userRecaptchaToken = null;
      this.userInfo.recaptchaToken = null;
      this.messageBox.showErrorMessage(
        'Invalid Token',
        this.validationErrorMessage
      );
      this.checkRecaptchaValidation();
    } else {
      this.errorService.displayError(error);
    }
  }

  isValidCommunityNotification(obj: any): boolean {
    let result = false;
    if (obj.fieldName.includes('1') || obj.fieldName.includes('4')) {
      result = true;
    }
    return result;
  }

  onEmailOptionsRecaptchaResolved(event): void {
    this.onRecaptchaResolved(event);
    this.checkRecaptchaValidation();
  }

  private checkRecaptchaValidation(): void {
    this.isRecaptchaResolved =
      this.isValidRecaptcha() || Utils.notNullOrEmpty(this.userRecaptchaToken);
  }

  private prepareCommunicationsCustomFields(): void {
    const customFieldsData: Array<AddContactInfoCustomFieldRawDto> = [];
    this.customFieldsData.forEach((customField) => {
      customFieldsData.push({
        fieldName: customField.fieldName,
        fieldValue: customField.fieldValue
      });
    });
    this.userInfo.contactInfo.emailData.customFieldsData = customFieldsData;
    this.userInfo.contactInfo.emailData.subTypeId = !!this.userInfo.contactInfo
      .emailData.subTypeId
      ? this.userInfo.contactInfo.emailData.subTypeId
      : this.registrationEditor.defaultEmailSubTypeId;
  }

  private prepareCommunications(): void {
    if (this.registrationEditor.emailCustomDataFields.length > 0) {
      this.registrationEditor.emailCustomDataFields.forEach((field) => {
        let emailField: AddContactInfoCustomFieldRawDto =
          new AddContactInfoCustomFieldRawDto();
        if (
          !!this.userInfo.contactInfo.emailData &&
          !!this.userInfo.contactInfo.emailData.customFieldsData &&
          this.userInfo.contactInfo.emailData.customFieldsData.length > 0
        ) {
          emailField =
            this.userInfo.contactInfo.emailData.customFieldsData.find(
              (d) => d.fieldName.toLowerCase() === field.fieldName.toLowerCase()
            );
        }

        this.customFieldsData.push({
          fieldName: field.fieldName,
          fieldValue:
            !!emailField &&
            !!emailField.fieldName &&
            (emailField.fieldValue === true || emailField.fieldValue === false)
              ? emailField.fieldValue
              : true,
          caption: field.caption
        });
      });
    }
  }

  private updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
    this.isMediumScreen = this.screenService.isMediumScreen();
  }
}
